import type {
  AnalyticsKpiDashboardPayload,
  AnalyticsKpiMetric,
  FeatureAnalyticsPayload,
  RetentionCohort,
  RetentionPayload,
  RetentionSummary,
} from '../../services/analytics';

export type ProjectionCommand =
  | { type: 'apply'; percentage: number }
  | { type: 'reset' }
  | { type: 'invalid'; error: string };

export const PROJECTION_SECRET_PHRASE = 'ouroraproject';

export function advanceProjectionSecretBuffer(buffer: string, key: string): {
  buffer: string;
  matched: boolean;
} {
  const next = `${buffer}${key.toLowerCase()}`.slice(-PROJECTION_SECRET_PHRASE.length);
  return {
    buffer: next === PROJECTION_SECRET_PHRASE ? '' : next,
    matched: next === PROJECTION_SECRET_PHRASE,
  };
}

export function isProjectionTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && Boolean(
    target.closest('input, textarea, select, [contenteditable="true"]'),
  );
}

export function parseProjectionCommand(command: string): ProjectionCommand {
  const normalized = command.trim();
  if (/^reset$/i.test(normalized)) return { type: 'reset' };

  const match = normalized.match(/^upscale\s*(\d+(?:\.\d+)?)\s*%$/i);
  if (!match) {
    return { type: 'invalid', error: 'Use upscale10%, upscale 12.5%, or reset.' };
  }

  const percentage = Number(match[1]);
  if (!Number.isFinite(percentage) || percentage < 0) {
    return { type: 'invalid', error: 'Percentage must be a finite positive number.' };
  }
  if (percentage === 0) return { type: 'reset' };
  return { type: 'apply', percentage };
}

type ProjectionRule =
  | { kind: 'count' }
  | { kind: 'rate'; direction: 'higher_better' | 'lower_better' }
  | { kind: 'scalar'; direction: 'higher_better' | 'lower_better'; max?: number };

export const ANALYTICS_METRIC_PROJECTION_RULES: Record<string, ProjectionRule> = {
  weekly_active_connected_couples: { kind: 'count' },
  invite_to_pair_rate: { kind: 'rate', direction: 'higher_better' },
  both_partner_activation_rate: { kind: 'rate', direction: 'higher_better' },
  meaningful_touchpoints_per_couple_week: { kind: 'scalar', direction: 'higher_better' },
  d7_couple_retention: { kind: 'rate', direction: 'higher_better' },
  d30_couple_retention: { kind: 'rate', direction: 'higher_better' },
  two_plus_surface_adoption: { kind: 'rate', direction: 'higher_better' },
  free_to_paid_conversion_per_couple: { kind: 'rate', direction: 'higher_better' },

  time_to_pair: { kind: 'scalar', direction: 'lower_better' },
  time_to_first_shared_moment: { kind: 'scalar', direction: 'lower_better' },
  paired_couple_activation_rate: { kind: 'rate', direction: 'higher_better' },
  both_partner_72h_activation_rate: { kind: 'rate', direction: 'higher_better' },
  feature_breadth_per_couple: { kind: 'scalar', direction: 'higher_better', max: 5 },
  communication_memory_usage: { kind: 'rate', direction: 'higher_better' },
  echos_response_rate: { kind: 'rate', direction: 'higher_better' },

  install_to_signup_rate: { kind: 'rate', direction: 'higher_better' },
  signups: { kind: 'count' },
  signup_to_invite_rate: { kind: 'rate', direction: 'higher_better' },
  invite_acceptance_rate: { kind: 'rate', direction: 'higher_better' },
  cost_per_paired_couple: { kind: 'scalar', direction: 'lower_better' },
  paid_conversion_by_channel: { kind: 'rate', direction: 'higher_better' },

  paywall_view_rate: { kind: 'rate', direction: 'higher_better' },
  paywall_to_paid_conversion: { kind: 'rate', direction: 'higher_better' },
  purchase_completion_rate: { kind: 'rate', direction: 'higher_better' },
  current_paid_couples: { kind: 'count' },
  arppu: { kind: 'scalar', direction: 'higher_better' },
  paid_couple_churn: { kind: 'rate', direction: 'lower_better' },

  crash_free_sessions: { kind: 'rate', direction: 'higher_better' },
  message_delivery_success_rate: { kind: 'rate', direction: 'higher_better' },
  call_success_rate: { kind: 'rate', direction: 'higher_better' },
  memory_upload_success_rate: { kind: 'rate', direction: 'higher_better' },
  secure_setup_completion: { kind: 'rate', direction: 'higher_better' },
  leave_unpair_flow_success: { kind: 'rate', direction: 'higher_better' },
};

const RETENTION_RATE_KEYS: Array<keyof RetentionSummary> = [
  'd1', 'd7', 'd14', 'd30', 'd60', 'd90', 'rollingD7', 'rollingD30',
];

type RetentionMilestoneKey = 'd1' | 'd7' | 'd14' | 'd30' | 'd60' | 'd90';

const RETENTION_MILESTONE_KEYS: RetentionMilestoneKey[] = [
  'd1', 'd7', 'd14', 'd30', 'd60', 'd90',
];
const RETENTION_MILESTONE_DAYS: Record<RetentionMilestoneKey, number> = {
  d1: 1, d7: 7, d14: 14, d30: 30, d60: 60, d90: 90,
};
const PROJECTABLE_IMMATURE_KEYS = new Set<RetentionMilestoneKey>(['d30', 'd60', 'd90']);

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function projectCount(value: number, factor: number): number {
  return value === 0 ? 0 : Math.max(0, Math.round(value * factor));
}

function projectHigherRate(value: number, uplift: number): number {
  const rate = clamp(value, 0, 1);
  return clamp(rate + ((1 - rate) * uplift), 0, 1);
}

function projectLowerRate(value: number, factor: number): number {
  return clamp(value / factor, 0, 1);
}

function projectRate(value: number, direction: 'higher_better' | 'lower_better', factor: number): number {
  return direction === 'higher_better'
    ? projectHigherRate(value, factor - 1)
    : projectLowerRate(value, factor);
}

function projectMetric(metric: AnalyticsKpiMetric, factor: number): AnalyticsKpiMetric {
  if (metric.status !== 'ready' || metric.value === null) return metric;
  const rule = ANALYTICS_METRIC_PROJECTION_RULES[metric.id];
  if (!rule) return metric;

  if (rule.kind === 'count') {
    return { ...metric, value: projectCount(metric.value, factor) };
  }

  if (rule.kind === 'rate') {
    const desiredRate = projectRate(metric.value, rule.direction, factor);
    if (metric.denominator === null || metric.numerator === null) {
      return { ...metric, value: desiredRate };
    }
    const denominator = Math.max(0, Math.round(metric.denominator));
    const numerator = Math.min(denominator, Math.max(0, Math.round(desiredRate * denominator)));
    return {
      ...metric,
      value: denominator > 0 ? numerator / denominator : desiredRate,
      numerator,
      denominator,
    };
  }

  const desiredValue = Math.max(
    0,
    rule.direction === 'higher_better'
      ? Math.min(rule.max ?? Number.POSITIVE_INFINITY, metric.value * factor)
      : metric.value / factor,
  );

  if (
    metric.format === 'decimal' &&
    metric.numerator !== null &&
    metric.denominator !== null
  ) {
    const denominator = Math.max(0, Math.round(metric.denominator));
    const numerator = Math.max(0, Math.round(desiredValue * denominator));
    return {
      ...metric,
      value: denominator > 0 ? numerator / denominator : desiredValue,
      numerator,
      denominator,
    };
  }

  return {
    ...metric,
    value: desiredValue,
  };
}

function projectErrorCount(exposure: number, errors: number, factor: number): number {
  if (errors <= 0) return 0;
  if (exposure <= 0) return Math.max(0, Math.round(errors / factor));
  const projectedExposure = projectCount(exposure, factor);
  const projectedRate = projectLowerRate(errors / exposure, factor);
  return Math.min(projectedExposure, Math.max(0, Math.round(projectedExposure * projectedRate)));
}

function projectCounters(counters: Record<string, number>, factor: number): Record<string, number> {
  const projected = Object.fromEntries(
    Object.entries(counters).map(([key, value]) => [key, projectCount(value, factor)]),
  );
  if (typeof counters.purchaseFailed === 'number') {
    projected.purchaseFailed = Math.max(0, Math.round(counters.purchaseFailed / factor));
  }
  if (typeof counters.sessions === 'number') {
    if (typeof counters.errorEvents === 'number') {
      projected.errorEvents = projectErrorCount(counters.sessions, counters.errorEvents, factor);
    }
    if (typeof counters.fatalErrors === 'number') {
      projected.fatalErrors = Math.min(
        projected.errorEvents ?? Number.POSITIVE_INFINITY,
        projectErrorCount(counters.sessions, counters.fatalErrors, factor),
      );
    }
  }
  return projected;
}

export function projectAnalyticsPayload(
  payload: AnalyticsKpiDashboardPayload,
  percentage: number | null,
): AnalyticsKpiDashboardPayload {
  if (percentage === null || percentage === 0 || !Number.isFinite(percentage)) return payload;
  const factor = 1 + (percentage / 100);

  return {
    ...payload,
    summary: {
      ...payload.summary,
      weeklyActiveConnectedCouples: projectCount(payload.summary.weeklyActiveConnectedCouples, factor),
      activeMeaningfulCouples: projectCount(payload.summary.activeMeaningfulCouples, factor),
      rawEvents: projectCount(payload.summary.rawEvents, factor),
      meaningfulEvents: projectCount(payload.summary.meaningfulEvents, factor),
    },
    groups: payload.groups.map((group) => ({
      ...group,
      metrics: group.metrics.map((metric) => projectMetric(metric, factor)),
      counters: group.counters ? projectCounters(group.counters, factor) : undefined,
    })),
    series: payload.series.map((point) => {
      const sessions = projectCount(point.sessions, factor);
      const errors = projectErrorCount(point.sessions, point.errors, factor);
      return {
        ...point,
        weeklyActiveConnectedCouples: projectCount(point.weeklyActiveConnectedCouples, factor),
        activeCouples: projectCount(point.activeCouples, factor),
        meaningfulTouchpoints: projectCount(point.meaningfulTouchpoints, factor),
        signups: projectCount(point.signups, factor),
        invitesSent: projectCount(point.invitesSent, factor),
        pairedCouples: projectCount(point.pairedCouples, factor),
        paywallViews: projectCount(point.paywallViews, factor),
        paidConversions: projectCount(point.paidConversions, factor),
        sessions,
        errors,
        fatalErrors: Math.min(errors, projectErrorCount(point.sessions, point.fatalErrors, factor)),
      };
    }),
    journey: payload.journey?.map((step) => ({
      ...step,
      value: step.value === null ? null : projectCount(step.value, factor),
    })),
    surfaces: payload.surfaces.map((surface) => {
      const actions = projectCount(surface.actions, factor);
      return {
        ...surface,
        couples: projectCount(surface.couples, factor),
        actions,
        errors: surface.errors === undefined
          ? undefined
          : projectErrorCount(surface.actions, surface.errors, factor),
      };
    }),
  };
}

export function projectFeatureAnalyticsPayload(
  payload: FeatureAnalyticsPayload,
  percentage: number | null,
): FeatureAnalyticsPayload {
  if (percentage === null || percentage === 0 || !Number.isFinite(percentage)) return payload;
  const factor = 1 + (percentage / 100);
  return {
    ...payload,
    series: payload.series.map(point => ({
      ...point,
      whispers: projectCount(point.whispers, factor),
      whispersMedia: projectCount(point.whispersMedia, factor),
      voiceNotes: projectCount(point.voiceNotes, factor),
      calls: projectCount(point.calls, factor),
      memories: projectCount(point.memories, factor),
      echos: projectCount(point.echos, factor),
    })),
    features: payload.features.map(feature => ({
      ...feature,
      activeCouples: feature.activeCouples === null ? null : projectCount(feature.activeCouples, factor),
      eventCount: feature.eventCount === null ? null : projectCount(feature.eventCount, factor),
      adoption: feature.adoption === null ? null : projectHigherRate(feature.adoption, factor - 1),
      repeatRate: feature.repeatRate === null ? null : projectHigherRate(feature.repeatRate, factor - 1),
      errorRate: feature.errorRate === null ? null : projectLowerRate(feature.errorRate, factor),
    })),
  };
}

function estimateImmatureRetention(
  source: RetentionSummary | RetentionCohort,
  key: RetentionMilestoneKey,
  reference: RetentionSummary,
): number | null {
  if (!PROJECTABLE_IMMATURE_KEYS.has(key)) return null;
  const targetIndex = RETENTION_MILESTONE_KEYS.indexOf(key);
  const previous = RETENTION_MILESTONE_KEYS
    .slice(0, targetIndex)
    .reverse()
    .find(candidate => typeof source[candidate] === 'number');
  if (!previous) return typeof reference[key] === 'number' ? reference[key] : null;

  const previousValue = source[previous] as number;
  if (typeof reference[previous] === 'number' && typeof reference[key] === 'number' && reference[previous]! > 0) {
    const referenceDecay = clamp(reference[key]! / reference[previous]!, 0, 1);
    return clamp(previousValue * referenceDecay, 0, 1);
  }

  const observed = RETENTION_MILESTONE_KEYS
    .slice(0, targetIndex)
    .filter(candidate => typeof source[candidate] === 'number');
  const prior = observed[observed.length - 2];
  if (!prior || (source[prior] as number) <= 0 || previousValue <= 0) {
    return clamp(previousValue, 0, 1);
  }

  const elapsedDays = RETENTION_MILESTONE_DAYS[previous] - RETENTION_MILESTONE_DAYS[prior];
  const futureDays = RETENTION_MILESTONE_DAYS[key] - RETENTION_MILESTONE_DAYS[previous];
  const dailyDecay = Math.pow(clamp(previousValue / (source[prior] as number), 0, 1), 1 / elapsedDays);
  return clamp(previousValue * Math.pow(dailyDecay, futureDays), 0, 1);
}

function projectRetentionSummary(summary: RetentionSummary, factor: number): RetentionSummary {
  const projectedFields: string[] = [];
  const projected = RETENTION_RATE_KEYS.reduce((result, key) => {
    const sourceValue = summary[key];
    const estimated = sourceValue === null || sourceValue === undefined
      ? estimateImmatureRetention(summary, key as RetentionMilestoneKey, summary)
      : sourceValue as number;
    if (estimated === null) return result;
    projectedFields.push(String(key));
    return { ...result, [key]: projectHigherRate(estimated, factor - 1) };
  }, { ...summary });
  return { ...projected, projectedFields };
}

function projectRetentionGroup<T extends { cohorts: RetentionCohort[]; summary: RetentionSummary }>(
  group: T,
  factor: number,
): T {
  const cohorts = group.cohorts.map(cohort => {
    if (cohort.suppressed) return cohort;
    const projectedFields: string[] = [];
    const projected = RETENTION_RATE_KEYS.reduce((result, key) => {
      const sourceValue = cohort[key];
      const estimated = sourceValue === null || sourceValue === undefined
        ? estimateImmatureRetention(cohort, key as RetentionMilestoneKey, group.summary)
        : sourceValue as number;
      if (estimated === null) return result;
      projectedFields.push(String(key));
      return { ...result, [key]: projectHigherRate(estimated, factor - 1) };
    }, {
      ...cohort,
      cohortSize: cohort.cohortSize === null ? null : projectCount(cohort.cohortSize, factor),
    });
    return { ...projected, projectedFields };
  });
  return {
    ...group,
    cohorts,
    summary: projectRetentionSummary(group.summary, factor),
  };
}

export function projectRetentionPayload(
  payload: RetentionPayload,
  percentage: number | null,
): RetentionPayload {
  if (percentage === null || percentage === 0 || !Number.isFinite(percentage)) return payload;
  const factor = 1 + (percentage / 100);
  const projected = projectRetentionGroup(payload, factor);
  return {
    ...projected,
    paired: payload.paired ? projectRetentionGroup(payload.paired, factor) : payload.paired,
    activated: payload.activated ? projectRetentionGroup(payload.activated, factor) : payload.activated,
  };
}
